<template>
  <b-modal v-bind:id="id"
           class="basic-modal math-editor-modal"
           v-bind:title="$t('common.modal.math_editor.title')"
           v-on:ok="addMath"
           v-bind:ok-title="$t('common.confirm')"
           v-bind:close-title="$t('common.cancel')"
           v-on:cancel="clear">

    <nav class="navbar navbar-toggleable navbar-light">
      <ul class="nav navbar-nav">
        <li class="nav-item" v-bind:class="{ active: currentTab === 'basic' }">
          <a class="nav-link typography--body-3" v-on:click="currentTab = 'basic'">
            {{ $t('common.modal.math_editor.tabs.basic') }}
          </a>
        </li>
        <li class="nav-item" v-bind:class="{ active: currentTab === 'geometry' }">
          <a class="nav-link typography--body-3" v-on:click="currentTab = 'geometry'">
            {{ $t('common.modal.math_editor.tabs.geometry') }}
          </a>
        </li>
        <li class="nav-item" v-bind:class="{ active: currentTab === 'etc' }">
          <a class="nav-link typography--body-3" v-on:click="currentTab = 'etc'">
            {{ $t('common.modal.math_editor.tabs.etc') }}
          </a>
        </li>
      </ul>
    </nav>
    

    <div>
      <div class="width-100">
        <table class="table table-responsive math-cmd-table width-100">
          <!-- Basic tab -->
          <tbody v-show="currentTab === 'basic'">
            <!-- first row -->
            <tr>
              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('^', true)" title="Exponent" aria-label="Exponent">
                  <span class="mathquill-embedded-latex">x^{}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\sqrt', true)" title="Square root" aria-label="Square root">
                  <span class="mathquill-embedded-latex">\sqrt{}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('[', true)" title="Square brackets" aria-label="Square brackets">
                  <span class="mathquill-embedded-latex">\left[\right]</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\div', true)" title="Divide" aria-label="Divide">
                  <span class="mathquill-embedded-latex">\div</span>
                </button>
              </td>
              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('-', true)" title="Minus" aria-label="Minus">
                  <span class="mathquill-embedded-latex">-</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('=', true)" title="Equal to" aria-label="Equal to">
                  <span class="mathquill-embedded-latex">=</span>
                </button>
              </td>
              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\lt')" title="Less than" aria-label="Less than">
                  <span class="mathquill-embedded-latex">\lt</span>
                </button>
              </td>
              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\gt')" title="Greater than" aria-label="Greater than">
                  <span class="mathquill-embedded-latex">\gt</span>
                </button>
              </td>
            </tr>

            <!-- second row -->
            <tr>
              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('_', true)" title="Subscript" aria-label="Subscript">
                  <span class="mathquill-embedded-latex">x_{}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\sqrt[]{}')" title="Root" aria-label="Root">
                  <span class="mathquill-embedded-latex">\sqrt[]{}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('(', true)" title="Parentheses" aria-label="Parentheses">
                  <span class="mathquill-embedded-latex">\left(\right)</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\times', true)" title="Times" aria-label="Times">
                  <span class="mathquill-embedded-latex">\times</span>
                </button>
              </td>
              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('+', true)" title="Plus" aria-label="Plus">
                  <span class="mathquill-embedded-latex">+</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\neq')" title="Not equal to" aria-label="Not equal to">
                  <span class="mathquill-embedded-latex">\neq</span>
                </button>
              </td>
              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\leq')" title="Less than or equal to" aria-label="Less than or equal to">
                  <span class="mathquill-embedded-latex">\leq</span>
                </button>
              </td>
              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\geq')" title="Greater than or equal to" aria-label="Greater than or equal to">
                  <span class="mathquill-embedded-latex">\geq</span>
                </button>
              </td>
            </tr>

            <!-- third row -->
            <tr>
              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\frac', true)" title="Fraction" aria-label="Fraction">
                  <span class="mathquill-embedded-latex">\frac{}{}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box border-right empty-box"></td>

              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('{', true)" title="Curly brackets" aria-label="Curly brackets">
                  <span class="mathquill-embedded-latex">\left\{\right\}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box empty-box"></td>

              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\pm', true)" title="Plus minus" aria-label="Plus minus">
                  <span class="mathquill-embedded-latex">\pm</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\approx')" title="Approximately equal to" aria-label="Approximately equal to">
                  <span class="mathquill-embedded-latex">\approx</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\to')" title="Right arrow" aria-label="Right arrow">
                  <span class="mathquill-embedded-latex">\to</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\equiv')" title="Triple bar" aria-label="Triple bar">
                  <span class="mathquill-embedded-latex">\equiv</span>
                </button>
              </td>
            </tr>
          </tbody>


          <!-- Geometry tab -->
          <tbody v-show="currentTab === 'geometry'">
            <!-- first row -->
            <tr>
              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\angle', true)" title="Angle" aria-label="Angle">
                  <span class="mathquill-embedded-latex">\angle</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\perp', true)" title="Is perpendicular to" aria-label="Is perpendicular to">
                  <span class="mathquill-embedded-latex">\perp</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\overline', true)" title="Segment" aria-label="Segment">
                  <span class="mathquill-embedded-latex">\overline{xyz}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\overrightarrow', true)" title="Over right arrow" aria-label="Over right arrow">
                  <span class="mathquill-embedded-latex">\overrightarrow{xyz}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\slash', true)" title="Slash" aria-label="Slash">
                  <span class="mathquill-embedded-latex">\slash</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\triangle', true)" title="Triangle" aria-label="Triangle">
                  <span class="mathquill-embedded-latex">\triangle</span>
                </button>
              </td>
            </tr>

            <!-- second row -->
            <tr>
              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\measuredangle', true)" title="Measured angle" aria-label="Measured angle">
                  <span class="mathquill-embedded-latex">\measuredangle</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('^{\\circ}')" title="Degree" aria-label="Degree">
                  <span class="mathquill-embedded-latex">^{\circ}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\overarc', true)" title="Over arc" aria-label="Over arc">
                  <span class="mathquill-embedded-latex">\overarc{xyz}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\textrm', true)" title="Text" aria-label="Text">
                  <span class="mathquill-embedded-latex">\textrm{AB}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\square', true)" title="Square" aria-label="Square">
                  <span class="mathquill-embedded-latex">\square</span>
                </button>
              </td>
            </tr>

            <!-- third row -->
            <tr>
              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('|', true)" title="Distance" aria-label="Distance">
                  <span class="mathquill-embedded-latex">\left| \right|</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\sim', true)" title="Similarity" aria-label="Similarity">
                  <span class="mathquill-embedded-latex">\sim</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\overleftrightarrow', true)" title="Over left right arrow" aria-label="Over left right arrow">
                  <span class="mathquill-embedded-latex">\overleftrightarrow{xyz}</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\pi', true)" title="Pi" aria-label="Pi">
                  <span class="mathquill-embedded-latex">\pi</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\bigcirc', true)" title="Big circle" aria-label="Big circle">
                  <span class="mathquill-embedded-latex">\bigcirc</span>
                </button>
              </td>
            </tr>
          </tbody>


          <!-- Etc tab -->
          <tbody v-show="currentTab === 'etc'">
            <!-- first row -->
            <tr>
              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\infty', true)" title="Infinity" aria-label="Infinity">
                  <span class="mathquill-embedded-latex">\infty</span>
                </button>
              </td>

              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\therefore', true)" title="Therefore" aria-label="Therefore">
                  <span class="mathquill-embedded-latex">\therefore</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\sin', true)" title="Sine" aria-label="Sine">
                  <span class="mathquill-embedded-latex">\sin</span>
                </button>
              </td>
              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\ln', true)" title="Natural log" aria-label="Natural log">
                  <span class="mathquill-embedded-latex">\ln</span>
                </button>
              </td>
            </tr>

            <!-- second row -->
            <tr>
              <td class="math-cmd-btn-box border-right empty-box"></td>

              <td class="math-cmd-btn-box border-right">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\because')" title="Because" aria-label="Because">
                  <span class="mathquill-embedded-latex">\because</span>
                </button>
              </td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\cos', true)" title="Cosine" aria-label="Cosine">
                  <span class="mathquill-embedded-latex">\cos</span>
                </button>
              </td>
            </tr>

            <!-- third row -->
            <tr>
              <td class="math-cmd-btn-box border-right empty-box"></td>

              <td class="math-cmd-btn-box border-right empty-box"></td>

              <td class="math-cmd-btn-box">
                <button class="math-cmd-btn" v-on:mousedown.prevent="writeMathCmd('\\tan', true)" title="Tangent" aria-label="Tangent">
                  <span class="mathquill-embedded-latex">\tan</span>
                </button>
              </td>
            </tr>
          </tbody>

        </table>
      </div>

      <div class="math-editor-field-container">
        <span class="math-editor-field"></span>
      </div>
    </div>
  </b-modal>
</template>

<script>
  import { map } from 'lodash';

  // eslint-disable-next-line
  const MathQuill = require('exports-loader?window.MathQuill!imports-loader?window.jQuery=jquery!mathquill/build/mathquill.min.js');

  export default {
    name: 'math-editor-modal',

    props: [
      'id',
      'config',
    ],

    data() {
      return {
        mathField: null,

        currentTab: 'basic',
      };
    },

    methods: {
      writeMathCmd(latex, isCmd) {
        if (this.mathField && isCmd) {
          this.mathField.cmd(latex);
          this.mathField.focus();
        } else if (this.mathField && !isCmd) {
          this.mathField.write(latex);
          this.mathField.focus();
        }
      },

      getLatex() {
        return this.mathField.latex();
      },

      clear() {
        this.mathField.latex('');
      },

      addMath() {
        this.$emit('add', this.getLatex());
        this.clear();
      },
    },

    mounted() {
      const MQ = MathQuill.getInterface(2);

      /* eslint-disable new-cap */
      map(this.$el.getElementsByClassName('mathquill-embedded-latex'), el => MQ.StaticMath(el));

      this.mathField = MQ.MathField(this.$el.getElementsByClassName('math-editor-field')[0]);
      /* eslint-enable */

      this.mathField.focus();
    },
  };
</script>

<style lang="scss">
  .math-editor-modal {
    .modal-dialog {
      max-width: 580px;
      width: 100%;
      margin-top: 10vh;
    }

    .modal-body {
      padding-top: 0;
      padding-bottom: 8px;
    }

    .navbar {
      padding: 0;
      padding-bottom: 12px;
    }

    .navbar-nav {
      flex-direction: row;
    }

    .navbar-nav>.nav-item>.nav-link {
      border-bottom: 2px solid transparent;
      box-sizing: border-box;
      font-size: 14px;
      line-height: 1.29;
      padding: 8px 0.9em;
      padding-top: 0;
      color: rgba(0, 0, 0, 0.54) !important;
    }

    .navbar-nav>.nav-item.active .nav-link {
      border-bottom: 2px solid rgba(0, 0, 0, 0.54);
      font-weight: bold;
    }
    .math-cmd-table {
      border-collapse: collapse;
      box-sizing: border-box;
      border: 1px solid rgba(0, 0, 0, 0.12);

      tbody {
        border: none;
      }

      .mq-math-mode .mq-hasCursor:not(.mq-root-block) {
        background-color: #e6e6e6;
      }

      .mq-scaled.mq-paren {
        transform: scale(0.998758, 1.19255) !important;
      }

      .mq-scaled.mq-sqrt-prefix {
        transform: scale(1, 0.955901) !important;
      }
    }

    .math-cmd-btn-box {
      box-sizing: border-box;
      padding: 0;
      border: none;

      &.border-right {
        border-right: 1px solid rgba(0, 0, 0, 0.12);
      }
    }

    .math-cmd-btn-box:hover:not(.empty-box) {
      background: rgba(0, 0, 0, 0.06);
    }

    .math-cmd-btn-box:active:not(.empty-box) {
      background: rgba(0, 0, 0, 0.12);
    }  

    .math-cmd-btn-box * {
      cursor: pointer;
    }

    .math-cmd-btn {
      background-color: transparent;
      border: none;
      font-size: 18px;
      width: 60px;
      height: 60px;
      // line-height: 2.8em;
      // padding: 2px 6px 3px 6px;
      vertical-align: top;
      text-align: center;
    }

    .math-editor-field-container {
    }

    .math-editor-field {
      box-sizing: border-box;
      border-radius: 4px;
    	border: solid 1px rgba(0, 0, 0, 0.26);
      padding: 20px;
      width: 100%;
      box-shadow: none !important;

      &:hover {
        border: solid 1px #b3b3b3;
      }

      &:focus,
      &:active,
      &.mq-focused {
        outline: none;
        border: solid 1px #999999;
      }
    }

    .mq-math-mode .mq-empty:not(.mq-root-block) {
      background-color: #e6e6e6;
    }
  }
</style>



// WEBPACK FOOTER //
// src/app/my-cards/components/MathEditorModal.vue